<template>
  <div>
    <h2 class="text-h5 mb-4">
      Источники
    </h2>

    <analytics-filters
      v-model="filters"
      @apply="load"
    ></analytics-filters>

    <v-alert
      v-if="error"
      type="error"
      dense
      class="mb-4"
    >
      {{ error }}
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :items-per-page="50"
      class="elevation-0 clickable-table"
      hide-default-footer
      @click:row="applySourceFilter"
    >
      <template #item.source="{ item }">
        <span :class="{ 'grey--text': isWeakSource(item.source) }">
          {{ item.source }}
        </span>
      </template>
      <template #item.revenue="{ item }">
        {{ formatMoney(item.revenue) }} ₽
      </template>
      <template #item.cr="{ item }">
        {{ item.cr }}%
      </template>
      <template #item.actions="{ item }">
        <v-btn
          text
          x-small
          color="primary"
          @click.stop="openSourceCustomers(item)"
        >
          Клиенты
        </v-btn>
      </template>
    </v-data-table>

    <h3 class="text-h6 mt-8 mb-3">
      Кампании и объявления
    </h3>
    <v-data-table
      :headers="campaignHeaders"
      :items="campaigns"
      :loading="campaignsLoading"
      :items-per-page="50"
      class="elevation-0 clickable-table"
      hide-default-footer
      @click:row="applyCampaignFilter"
    >
      <template #item.source="{ item }">
        <span :class="{ 'grey--text': isWeakSource(item.source) }">
          {{ item.source }}
        </span>
      </template>
      <template #item.campaign="{ item }">
        {{ item.campaign || '—' }}
      </template>
      <template #item.ad_id="{ item }">
        {{ item.ad_id || '—' }}
      </template>
      <template #item.revenue="{ item }">
        {{ formatMoney(item.revenue) }} ₽
      </template>
      <template #item.actions="{ item }">
        <v-btn
          text
          x-small
          color="primary"
          @click.stop="openCampaignCustomers(item)"
        >
          Клиенты
        </v-btn>
      </template>
    </v-data-table>

    <v-alert
      type="info"
      text
      dense
      class="mt-4"
    >
      CR = купили / визиты. Визиты — сессии, лиды — уникальные клиенты с заявкой,
      «пришли» и «купили» — уникальные клиенты по выбранной модели атрибуции
      (кому засчитать клиента в источниках).
      <br>
      <strong>direct</strong> — заход без метки и без внешнего сайта;
      <strong>internal</strong> — переход внутри сайта;
      <strong>payment return</strong> — возврат с оплаты.
      Клик по строке ставит фильтр и обновляет таблицы;
      кнопка «Клиенты» открывает список.
    </v-alert>
  </div>
</template>

<script>
import Api from '@/api/api'
import AnalyticsFilters from './AnalyticsFilters.vue'

const WEAK_SOURCES = new Set([
  'direct / unknown',
  'direct',
  'internal',
  'payment return',
])

function stripEmpty(obj) {
  const out = {}
  Object.keys(obj || {}).forEach(k => {
    const v = obj[k]
    if (v !== '' && v !== null && v !== undefined) out[k] = v
  })
  return out
}

export default {
  name: 'AnalyticsSources',
  components: { AnalyticsFilters },
  data() {
    return {
      filters: {
        date_from: '',
        date_to: '',
        attribution: 'lead',
        source: '',
        campaign: '',
        company_id: null,
      },
      items: [],
      campaigns: [],
      loading: false,
      campaignsLoading: false,
      error: null,
      headers: [
        { text: 'Источник', value: 'source' },
        { text: 'Визиты', value: 'visits', align: 'end' },
        { text: 'Лиды', value: 'leads', align: 'end' },
        { text: 'Пришли', value: 'came', align: 'end' },
        { text: 'Купили', value: 'buyers', align: 'end' },
        { text: 'CR', value: 'cr', align: 'end' },
        { text: 'Выручка', value: 'revenue', align: 'end' },
        { text: '', value: 'actions', sortable: false },
      ],
      campaignHeaders: [
        { text: 'Источник', value: 'source' },
        { text: 'Кампания', value: 'campaign' },
        { text: 'Объявление', value: 'ad_id' },
        { text: 'Визиты', value: 'visits', align: 'end' },
        { text: 'С yclid', value: 'with_yclid', align: 'end' },
        { text: 'Лиды', value: 'leads', align: 'end' },
        { text: 'Купили', value: 'buyers', align: 'end' },
        { text: 'Выручка', value: 'revenue', align: 'end' },
        { text: '', value: 'actions', sortable: false },
      ],
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    isWeakSource(source) {
      return WEAK_SOURCES.has(source)
    },
    formatMoney(v) {
      return Number(v || 0).toLocaleString('ru-RU', {
        maximumFractionDigits: 0,
      })
    },
    applySourceFilter(item) {
      if (!item) return
      this.filters = {
        ...this.filters,
        source: item.source,
        campaign: '',
      }
      this.load()
    },
    applyCampaignFilter(item) {
      if (!item) return
      this.filters = {
        ...this.filters,
        source: item.source,
        campaign: item.campaign || '',
      }
      this.load()
    },
    openSourceCustomers(item) {
      if (!item) return
      this.$router.push({
        name: 'AnalyticsCustomers',
        query: stripEmpty({
          ...this.filters,
          source: item.source,
        }),
      })
    },
    openCampaignCustomers(item) {
      if (!item) return
      this.$router.push({
        name: 'AnalyticsCustomers',
        query: stripEmpty({
          ...this.filters,
          source: item.source,
          campaign: item.campaign,
          content: item.content,
        }),
      })
    },
    async load() {
      this.loading = true
      this.campaignsLoading = true
      this.error = null
      try {
        const data = await Api.getAnalyticsSources(stripEmpty(this.filters))
        this.items = data.items || []
      } catch (e) {
        this.error = 'Не удалось загрузить отчёт по источникам'
        this.items = []
      } finally {
        this.loading = false
      }
      try {
        const data = await Api.getAnalyticsCampaigns(stripEmpty(this.filters))
        this.campaigns = data.items || []
      } catch (e) {
        this.campaigns = []
      } finally {
        this.campaignsLoading = false
      }
    },
  },
}
</script>

<style scoped>
.clickable-table >>> tbody tr {
  cursor: pointer;
}
</style>
